<template>
  <div class="min-h-screen bg-gradient-to-b from-white to-gray-50">
    <div class="container mx-auto px-4 py-16">
      <!-- Logo Section -->
      <div class="text-center mb-12">
        <img src="/logo.svg" alt="Lessons Learned" class="mx-auto h-32 w-auto mb-8" />
      </div>

      <!-- Hero Section -->
      <div class="text-center mb-16 max-w-4xl mx-auto">
        <h1 class="text-5xl md:text-6xl font-bold mb-6 text-ll-blue">
          Expert Consultants in Financial Crime Compliance
        </h1>
        <p class="text-xl md:text-2xl text-gray-700 leading-relaxed">
          Lessons Learned is a consultancy whose unique approach gets your management and staff thinking about risks, making better decisions and applying the lessons of the past, enabling you to move forward responsibly to meet your strategic goals.
        </p>
      </div>

      <!-- Mission Statement -->
      <div class="max-w-4xl mx-auto mb-16 text-center">
        <p class="text-lg text-gray-700 leading-relaxed mb-4">
          We work with multilateral institutions, private sector corporations and NGOs to help promote and embed best practice in financial crime compliance and business ethics and integrity through leadership, governance, policies and training.
        </p>
      </div>

      <!-- Free Resources Section -->
      <div class="max-w-6xl mx-auto">
        <h2 class="text-4xl md:text-5xl font-bold text-ll-blue text-center mb-12">
          Free Resources
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- AML Training Guide Card -->
          <div class="bg-white rounded-lg overflow-hidden shadow-xl border-2 border-gray-200 hover:border-ll-blue transition-all duration-300 hover:transform hover:scale-105">
            <div class="p-8">
              <div class="bg-ll-blue rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>

              <h3 class="text-2xl font-bold text-ll-blue mb-4">
                Role-Based AML Training for UAE LFIs
              </h3>

              <p class="text-gray-700 mb-6 leading-relaxed">
                A practical guide for MLROs and Compliance Leads on implementing effective role-based AML training programmes in UAE Licensed Financial Institutions.
              </p>

              <ul class="text-sm text-gray-600 mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-ll-red mr-2 font-bold">✓</span>
                  <span>Role-based training frameworks</span>
                </li>
                <li class="flex items-start">
                  <span class="text-ll-red mr-2 font-bold">✓</span>
                  <span>UAE-specific regulatory requirements</span>
                </li>
                <li class="flex items-start">
                  <span class="text-ll-red mr-2 font-bold">✓</span>
                  <span>Practical implementation strategies</span>
                </li>
                <li class="flex items-start">
                  <span class="text-ll-red mr-2 font-bold">✓</span>
                  <span>Real-world case studies</span>
                </li>
              </ul>

              <router-link
                to="/aml-training-guide"
                class="block w-full text-center bg-ll-red hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
              >
                Download Free Guide
              </router-link>
            </div>
          </div>

          <!-- Integrity Framework Guide Card -->
          <div class="bg-white rounded-lg overflow-hidden shadow-xl border-2 border-gray-200 hover:border-ll-blue transition-all duration-300 hover:transform hover:scale-105">
            <div class="p-8">
              <div class="bg-ll-blue rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>

              <h3 class="text-2xl font-bold text-ll-blue mb-4">
                Integrity Activities Benchmarking Framework
              </h3>

              <p class="text-gray-700 mb-6 leading-relaxed">
                A practical resource for integrity, compliance, and anti-corruption professionals in international financial institutions.
              </p>

              <ul class="text-sm text-gray-600 mb-6 space-y-2">
                <li class="flex items-start">
                  <span class="text-ll-red mr-2 font-bold">✓</span>
                  <span>10-domain integrity operating model</span>
                </li>
                <li class="flex items-start">
                  <span class="text-ll-red mr-2 font-bold">✓</span>
                  <span>Governance & strategic alignment</span>
                </li>
                <li class="flex items-start">
                  <span class="text-ll-red mr-2 font-bold">✓</span>
                  <span>Due diligence & preventive controls</span>
                </li>
                <li class="flex items-start">
                  <span class="text-ll-red mr-2 font-bold">✓</span>
                  <span>Integrity maturity overview & planning template</span>
                </li>
              </ul>

              <router-link
                to="/integrity-framework"
                class="block w-full text-center bg-ll-red hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
              >
                Download Free Framework
              </router-link>
            </div>
          </div>

          <!-- Placeholder Card 3 - Future Resource -->
          <div class="bg-white rounded-lg overflow-hidden shadow-xl border-2 border-gray-200 opacity-60">
            <div class="p-8">
              <div class="bg-ll-gray rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>

              <h3 class="text-2xl font-bold text-gray-600 mb-4">
                Training Materials
              </h3>

              <p class="text-gray-600 mb-6 leading-relaxed">
                Coming soon: Bespoke training materials and resources for financial crime compliance teams.
              </p>

              <button
                disabled
                class="w-full bg-gray-300 text-gray-500 font-semibold py-3 px-6 rounded-lg cursor-not-allowed"
              >
                Coming Soon
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Services Overview Section -->
      <div class="max-w-6xl mx-auto mt-20 mb-16">
        <h2 class="text-4xl md:text-5xl font-bold text-ll-blue text-center mb-12">
          Our Services
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="text-center">
            <div class="bg-ll-blue rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-ll-blue mb-4">Bespoke Face to Face</h3>
            <p class="text-gray-700">
              Tailored in-person training sessions designed to get your teams thinking critically about risks and compliance.
            </p>
          </div>

          <div class="text-center">
            <div class="bg-ll-blue rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-ll-blue mb-4">Consultancy</h3>
            <p class="text-gray-700">
              Strategic guidance on financial crime compliance, governance frameworks, and best practice implementation.
            </p>
          </div>

          <div class="text-center">
            <div class="bg-ll-blue rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-ll-blue mb-4">Policy Development</h3>
            <p class="text-gray-700">
              Development of robust policies and procedures to embed compliance and ethical business practices.
            </p>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="max-w-4xl mx-auto mt-20 bg-ll-blue rounded-lg p-12 text-center shadow-2xl">
        <h2 class="text-3xl md:text-4xl font-bold text-white mb-6">
          Ready to Strengthen Your Compliance Framework?
        </h2>
        <p class="text-lg text-white mb-8 opacity-90">
          Discover how Lessons Learned can help your organisation build robust financial crime compliance and business ethics frameworks. Get in touch to discuss your specific needs.
        </p>
        <a
          href="https://www.lessonslearned.co.uk/contact"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-block bg-ll-red hover:bg-red-700 text-white font-semibold py-4 px-8 rounded-lg text-lg transition-colors duration-300"
        >
          Get in Touch
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
  mounted() {
    document.title = 'Lessons Learned - Expert Consultants in Financial Crime Compliance';
  }
};
</script>

<style scoped>
/* Additional component-specific styles if needed */
</style>
